import { supabase } from '../lib/supabase';
import type { PlayerAttendance, AttendanceStats } from '../types/playerCard';

/**
 * Service for managing Player Attendance
 * Tracks attendance for practices and games
 */
class PlayerAttendanceService {
  /**
   * Log attendance exception for a player
   * Note: Only exceptions are tracked. If a player is present, no record is created.
   * 
   * @param status - 'late' (arrived after warmups), 'no_show' (did not attend), or 'excused' (excused absence)
   */
  async logAttendance(
    playerId: string,
    teamId: string,
    eventType: 'practice' | 'game',
    eventId: string,
    status: 'late' | 'no_show' | 'excused',
    eventDate: string,
    eventTime?: string,
    arrivalTime?: string,
    departureTime?: string,
    notes?: string
  ): Promise<PlayerAttendance> {
    const { data, error } = await supabase
      .from('player_attendance')
      .insert({
        player_id: playerId,
        team_id: teamId,
        event_type: eventType,
        event_id: eventId,
        event_date: eventDate,
        event_time: eventTime || null,
        status,
        arrival_time: arrivalTime || null,
        departure_time: departureTime || null,
        notes: notes || null,
      })
      .select()
      .single();

    if (error) {
      console.error('[PlayerAttendanceService] Error logging attendance:', error);
      throw error;
    }

    return data;
  }

  /**
   * Remove attendance exception (mark player as present)
   * This deletes the exception record, effectively marking the player as present
   */
  async removeAttendanceException(
    playerId: string,
    eventType: 'practice' | 'game',
    eventId: string
  ): Promise<void> {
    const { error } = await supabase
      .from('player_attendance')
      .delete()
      .eq('player_id', playerId)
      .eq('event_type', eventType)
      .eq('event_id', eventId);

    if (error) {
      console.error('[PlayerAttendanceService] Error removing attendance exception:', error);
      throw error;
    }
  }

  /**
   * Update attendance record
   */
  async updateAttendance(
    attendanceId: string,
    updates: Partial<PlayerAttendance>
  ): Promise<PlayerAttendance> {
    const { data, error } = await supabase
      .from('player_attendance')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', attendanceId)
      .select()
      .single();

    if (error) {
      console.error('[PlayerAttendanceService] Error updating attendance:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get attendance for a specific event
   */
  async getAttendanceForEvent(
    eventType: 'practice' | 'game',
    eventId: string
  ): Promise<PlayerAttendance[]> {
    const { data, error } = await supabase
      .from('player_attendance')
      .select('*')
      .eq('event_type', eventType)
      .eq('event_id', eventId)
      .order('event_date', { ascending: true });

    if (error) {
      console.error('[PlayerAttendanceService] Error fetching attendance:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get attendance history for a player
   */
  async getAttendanceHistory(
    playerId: string,
    teamId: string,
    startDate?: string,
    endDate?: string
  ): Promise<PlayerAttendance[]> {
    let query = supabase
      .from('player_attendance')
      .select('*')
      .eq('player_id', playerId)
      .eq('team_id', teamId)
      .order('event_date', { ascending: false });

    if (startDate) {
      query = query.gte('event_date', startDate);
    }

    if (endDate) {
      query = query.lte('event_date', endDate);
    }

    const { data, error } = await query;

    if (error) {
      console.error('[PlayerAttendanceService] Error fetching attendance history:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get attendance statistics for a player
   * Note: This requires knowing total practices/games to calculate present counts
   * (since only exceptions are tracked)
   */
  async getAttendanceStats(
    playerId: string,
    teamId: string,
    totalPractices?: number,
    totalGames?: number
  ): Promise<AttendanceStats> {
    // Get all attendance exception records for this player
    const { data: allAttendance, error } = await supabase
      .from('player_attendance')
      .select('*')
      .eq('player_id', playerId)
      .eq('team_id', teamId);

    if (error) {
      console.error('[PlayerAttendanceService] Error fetching attendance for stats:', error);
      throw error;
    }

    const attendance = allAttendance || [];

    // Separate practices and games
    const practices = attendance.filter(a => a.event_type === 'practice');
    const games = attendance.filter(a => a.event_type === 'game');

    // Count exceptions
    const practicesLate = practices.filter(a => a.status === 'late').length;
    const practicesNoShow = practices.filter(a => a.status === 'no_show').length;
    const practicesExcused = practices.filter(a => a.status === 'excused').length;

    const gamesLate = games.filter(a => a.status === 'late').length;
    const gamesNoShow = games.filter(a => a.status === 'no_show').length;
    const gamesExcused = games.filter(a => a.status === 'excused').length;

    // Calculate present counts (total - exceptions)
    // If totals not provided, we can't calculate accurately, so use exception counts as approximation
    const totalPracticesCount = totalPractices ?? practices.length;
    const totalGamesCount = totalGames ?? games.length;
    
    const practicesPresent = Math.max(0, totalPracticesCount - practices.length);
    const gamesPresent = Math.max(0, totalGamesCount - games.length);

    // Calculate attendance percentage
    // Only Late and No Show count as negative (Excused doesn't count against)
    const totalEvents = totalPracticesCount + totalGamesCount;
    const derogatoryExceptions = practicesLate + practicesNoShow + gamesLate + gamesNoShow;
    const attendancePercentage = totalEvents > 0
      ? Math.round(((totalEvents - derogatoryExceptions) / totalEvents) * 100)
      : 100;

    // Find last attended date (most recent event that wasn't a no_show)
    const attendedRecords = attendance.filter(a => a.status !== 'no_show');
    const lastAttended = attendedRecords.length > 0
      ? attendedRecords.sort((a, b) => 
          new Date(b.event_date).getTime() - new Date(a.event_date).getTime()
        )[0].event_date
      : undefined;

    return {
      total_practices: totalPracticesCount,
      total_games: totalGamesCount,
      practices_present: practicesPresent,
      practices_late: practicesLate,
      practices_no_show: practicesNoShow,
      practices_excused: practicesExcused,
      games_present: gamesPresent,
      games_late: gamesLate,
      games_no_show: gamesNoShow,
      games_excused: gamesExcused,
      attendance_percentage: attendancePercentage,
      last_attended: lastAttended,
    };
  }

  /**
   * Delete attendance record
   */
  async deleteAttendance(attendanceId: string): Promise<void> {
    const { error } = await supabase
      .from('player_attendance')
      .delete()
      .eq('id', attendanceId);

    if (error) {
      console.error('[PlayerAttendanceService] Error deleting attendance:', error);
      throw error;
    }
  }

  /**
   * Batch log attendance exceptions for multiple players
   * Note: Only exceptions should be included (late, no_show, excused)
   */
  async batchLogAttendance(
    attendanceRecords: Array<{
      player_id: string;
      team_id: string;
      event_type: 'practice' | 'game';
      event_id: string;
      status: 'late' | 'no_show' | 'excused';
      event_date: string;
      event_time?: string;
      arrival_time?: string;
      departure_time?: string;
      notes?: string;
    }>
  ): Promise<PlayerAttendance[]> {
    // Filter out empty arrays
    if (attendanceRecords.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from('player_attendance')
      .insert(attendanceRecords)
      .select();

    if (error) {
      console.error('[PlayerAttendanceService] Error batch logging attendance:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Upsert attendance exception (insert or update if exists)
   * Useful for updating a player's exception status
   */
  async upsertAttendanceException(
    playerId: string,
    teamId: string,
    eventType: 'practice' | 'game',
    eventId: string,
    status: 'late' | 'no_show' | 'excused',
    eventDate: string,
    eventTime?: string,
    notes?: string
  ): Promise<PlayerAttendance> {
    // First, try to find existing record
    const { data: existing } = await supabase
      .from('player_attendance')
      .select('*')
      .eq('player_id', playerId)
      .eq('event_type', eventType)
      .eq('event_id', eventId)
      .single();

    if (existing) {
      // Update existing record
      return this.updateAttendance(existing.id, {
        status,
        notes: notes || null,
        updated_at: new Date().toISOString(),
      });
    } else {
      // Create new record
      return this.logAttendance(
        playerId,
        teamId,
        eventType,
        eventId,
        status,
        eventDate,
        eventTime,
        undefined,
        undefined,
        notes
      );
    }
  }
}

export const playerAttendanceService = new PlayerAttendanceService();

